/**
 * Ground-Truth Score Calibration — grade drafts against BOTH the playbook
 * and this author's observed history, then backtest the scorer against
 * what posts actually did.
 *
 * Fixes the auditor's "score is a lint checker" and "no calibration"
 * findings. `scoreAgainst` layers a personal adjustment over the shipped
 * heuristic scorer (`lib/crm/contentScore` — reused, never reimplemented):
 * each dimension's base heuristic score is nudged by the author's own
 * evidenced signals (e.g. a question hook scores higher for an author whose
 * question hooks demonstrably lift engagement), weighted by the transparent
 * cold-start ramp so zero history means zero personal adjustment.
 * `lowestDimension` picks the weakest axis to drive the one-click rewrite,
 * and `backtest` compares predicted scores to ACTUAL engagement rates with
 * a Pearson correlation — the honest "is this score worth anything?" number.
 *
 * PURE by contract: no store, no firebase, no network, no clock.
 */

import { scoreDraft } from '../crm/contentScore';
import {
  countWords,
  engagementRate,
  extractHookStyle,
  type LoggedPost,
} from './postPerformance';
import { coldStartWeight, lengthBucket, type Signal, type SignalSet } from './whatWorks';
import type { PlaybookRule } from './playbookLoader';

// ---------------------------------------------------------------------------
// Dimensions
// ---------------------------------------------------------------------------

/** The calibrated breakdown's canonical axes, in fixed render order. */
export const CALIBRATION_DIMENSIONS = ['hook', 'specificity', 'cta', 'length'] as const;

const clamp100 = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/** Max points a personal signal can move one dimension at full weight. */
const PERSONAL_SWING = 20;

/** CTA phrases / links / reply-invites the clarity check recognizes. */
const CTA_LINE =
  /\b(sign up|buy|shop|order|register|subscribe|book|get started|try|join|download|reply|comment|share|dm|reach out|visit|learn more|check out|let'?s talk|grab|claim)\b|https?:\/\//i;

function nonEmptyLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

/** Presence + placement of the ask: one CTA on the final line scores best. */
function ctaScore(text: string): number {
  const lines = nonEmptyLines(text);
  if (lines.length === 0) return 0;
  const askIdxs = lines
    .map((l, i) => (CTA_LINE.test(l) || /\?\s*$/.test(l) ? i : -1))
    .filter((i) => i >= 0);
  if (askIdxs.length === 0) return 20;
  if (lines.length <= 2) return 35;
  if (askIdxs.length === 1 && askIdxs[0] === lines.length - 1) return 95;
  if (askIdxs.length === 1) return 55;
  return 45;
}

/** Find an evidenced signal matching dimension+value ('none' never counts). */
function evidencedSignal(
  signalSet: SignalSet,
  dimension: Signal['dimension'],
  value: string,
): Signal | undefined {
  return signalSet.signals.find(
    (s) => s.dimension === dimension && s.value === value && s.strength !== 'none',
  );
}

/** Personal nudge: (lift − 1) scaled by cold-start weight, capped at ±SWING. */
function personalAdjust(signal: Signal | undefined, weight: number): number {
  if (!signal || weight <= 0) return 0;
  const raw = (signal.lift - 1) * PERSONAL_SWING * weight;
  return Math.max(-PERSONAL_SWING, Math.min(PERSONAL_SWING, raw));
}

// ---------------------------------------------------------------------------
// scoreAgainst
// ---------------------------------------------------------------------------

/**
 * Grade a draft on hook / specificity / cta / length-for-format:
 * base heuristics from the shipped scorer (+ a presence-aware CTA check),
 * then a personal adjustment from this author's evidenced signals. The
 * playbook decides WHICH canonical dimensions are graded (a playbook with
 * no length rules doesn't grade length); an empty/unmatched playbook
 * honestly falls back to all four. Every score is bounded 0–100; overall
 * is their mean. Deterministic and pure.
 */
export function scoreAgainst(
  draft: string,
  signalSet: SignalSet,
  playbook: PlaybookRule[],
): { overall: number; breakdown: Record<string, number> } {
  const base = scoreDraft(draft, { platform: 'linkedin' });
  const pct = (key: string): number => {
    const d = base.dimensions.find((dim) => dim.key === key);
    return d && d.max > 0 ? (d.score / d.max) * 100 : 0;
  };

  const weight = coldStartWeight(signalSet.totalPosts);
  const hookSig = evidencedSignal(signalSet, 'hookStyle', extractHookStyle(draft));
  const lenSig = evidencedSignal(signalSet, 'length', lengthBucket(countWords(draft)));

  const allScores: Record<string, number> = {
    hook: clamp100(pct('hook') + personalAdjust(hookSig, weight)),
    specificity: clamp100(pct('specificity')),
    cta: clamp100(ctaScore(draft)),
    length: clamp100(pct('length') + personalAdjust(lenSig, weight)),
  };

  // The playbook scopes the graded dimensions; fall back to all four when
  // it covers none of them (never an empty breakdown).
  const covered = new Set(playbook.map((r) => r.dimension));
  const dims = CALIBRATION_DIMENSIONS.filter((d) => covered.has(d));
  const graded = dims.length > 0 ? dims : [...CALIBRATION_DIMENSIONS];

  const breakdown: Record<string, number> = {};
  for (const d of graded) breakdown[d] = allScores[d];

  const overall = clamp100(
    graded.reduce((sum, d) => sum + breakdown[d], 0) / graded.length,
  );
  return { overall, breakdown };
}

// ---------------------------------------------------------------------------
// lowestDimension — drives the one-click rewrite
// ---------------------------------------------------------------------------

/**
 * The weakest axis in a breakdown — the one the one-click rewrite should
 * fix first. Ties break to the earlier key (insertion order), so the fix
 * order is stable. Empty breakdown returns '' (nothing to fix).
 */
export function lowestDimension(breakdown: Record<string, number>): string {
  let lowest = '';
  let min = Number.POSITIVE_INFINITY;
  for (const [key, value] of Object.entries(breakdown)) {
    if (value < min) {
      min = value;
      lowest = key;
    }
  }
  return lowest;
}

// ---------------------------------------------------------------------------
// backtest — predicted vs actual calibration
// ---------------------------------------------------------------------------

/**
 * Backtest a scorer against ground truth: for every logged post, pair the
 * scorer's PREDICTED score with the post's ACTUAL engagement rate (as a
 * percentage), then compute the Pearson correlation between the two
 * series. Correlation is 0 when there are fewer than 2 posts or either
 * series has zero variance — never NaN, never a fabricated fit. Pairs come
 * back in input order, aligned by id.
 */
export function backtest(
  loggedPosts: LoggedPost[],
  scoreFn: (p: LoggedPost) => number,
): {
  predictedVsActual: { id: string; predicted: number; actual: number }[];
  correlation: number;
} {
  const predictedVsActual = loggedPosts.map((p) => ({
    id: p.id,
    predicted: scoreFn(p),
    actual: Math.round(engagementRate(p.metrics) * 10000) / 100,
  }));

  return { predictedVsActual, correlation: pearson(predictedVsActual) };
}

function pearson(pairs: { predicted: number; actual: number }[]): number {
  const n = pairs.length;
  if (n < 2) return 0;
  const meanP = pairs.reduce((s, p) => s + p.predicted, 0) / n;
  const meanA = pairs.reduce((s, p) => s + p.actual, 0) / n;
  let cov = 0;
  let varP = 0;
  let varA = 0;
  for (const { predicted, actual } of pairs) {
    const dp = predicted - meanP;
    const da = actual - meanA;
    cov += dp * da;
    varP += dp * dp;
    varA += da * da;
  }
  if (varP === 0 || varA === 0) return 0;
  const r = cov / Math.sqrt(varP * varA);
  // Clamp float drift; round for stable display + tests.
  return Math.round(Math.max(-1, Math.min(1, r)) * 10000) / 10000;
}
